// TradeHistory/TradeTable.jsx
import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  Grid,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Bolt as BoltIcon,
  CheckCircle as CheckCircleIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  History as HistoryIcon,
  SwapHoriz as SwapHorizIcon,
  RadioButtonChecked as SingleIcon,
  CallSplit as VariantIcon,
} from '@mui/icons-material';
import {
  COLORS,
  formatDateTime,
  formatPrice,
  formatPnL,
  formatPercent,
  formatDuration,
  calculateTimeDiff,
} from './constants';

// Deterministic color per variant — cycles through palette
const VARIANT_PALETTE = ['#00d4ff', '#8b5cf6', '#f59e0b', '#ec4899', '#10b981', '#f97316'];
const getVariantColor = (variantId, allVariantIds) => {
  const idx = allVariantIds ? allVariantIds.indexOf(variantId) : 0;
  return VARIANT_PALETTE[(idx < 0 ? 0 : idx) % VARIANT_PALETTE.length];
};

// ==================== SIGNAL BOX (single signal cell) ====================
const SignalBox = ({ label, icon: Icon, signal, color }) => (
  <Box
    sx={{
      p: 1.25,
      bgcolor: alpha(color, 0.06),
      borderRadius: 1,
      border: `1px solid ${alpha(color, 0.12)}`,
    }}
  >
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
      <Icon sx={{ color, fontSize: 12 }} />
      <Typography
        variant="caption"
        sx={{ color: COLORS.text.muted, fontSize: '0.6rem', fontWeight: 600 }}
      >
        {label}
      </Typography>
    </Stack>
    <Typography
      variant="body2"
      sx={{ color, fontWeight: 700, fontSize: '0.8rem' }}
    >
      {signal?.name?.toUpperCase() || '—'}
    </Typography>
    {signal?.time && (
      <Typography
        variant="caption"
        sx={{ color: COLORS.text.muted, display: 'block', fontSize: '0.65rem', mt: 0.5 }}
      >
        {formatDateTime(signal.time)}
        <br />
        <span style={{ color }}>{formatPrice(signal.price)}</span>
      </Typography>
    )}
  </Box>
);

// ==================== SIGNAL DISPLAY (condition-type-aware) ====================
const SignalDisplay = ({ label, data, color = COLORS.primary }) => {
  const type = data?.type;

  // ── Resolve the two (or one) signals from the condition shape ──
  let leftSignal = null;
  let rightSignal = null;
  let leftLabel = 'EARLY';
  let rightLabel = 'CONFIRM';
  let leftIcon = CallReceivedIcon;
  let rightIcon = CallMadeIcon;
  let leftColor = COLORS.primary;
  let rightColor = COLORS.success;
  let isSingle = false;

  if (type === 'time_window') {
    leftSignal = data.signal_a;
    rightSignal = data.signal_b;
    leftLabel = 'SIGNAL A';
    rightLabel = 'SIGNAL B';
    leftIcon = SwapHorizIcon;
    rightIcon = SwapHorizIcon;
  } else if (type === 'single_signal') {
    leftSignal = data.signal;
    isSingle = true;
    leftLabel = 'SIGNAL';
    leftIcon = SingleIcon;
  } else {
    // time_bound, simultaneous_signals, sequence_with_exclusion, sequence_exclusion_with_equality
    leftSignal = data.early_signal;
    rightSignal = data.confirmation_signal;
  }

  // ── Time diff between the two signals ──
  const timeDiff = !isSingle
    ? calculateTimeDiff(leftSignal?.time, rightSignal?.time)
    : null;

  // ── Offset info for time_window ──
  const offsetInfo = type === 'time_window' && data.actual_offset_minutes != null
    ? `${data.actual_offset_minutes >= 0 ? '+' : ''}${data.actual_offset_minutes}m offset`
    : null;

  // ── Constraint badge ──
  let constraintLabel = null;
  if (type === 'time_bound' && data.min_minutes != null && data.max_minutes != null) {
    constraintLabel = `${data.min_minutes}–${data.max_minutes}m`;
  } else if (type === 'time_window' && data.min_offset_minutes != null && data.max_offset_minutes != null) {
    constraintLabel = `${data.min_offset_minutes} to ${data.max_offset_minutes}m`;
  }

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: alpha(color, 0.05),
        border: `1px solid ${alpha(color, 0.15)}`,
        borderRadius: 1.5,
        mb: 1,
      }}
    >
      {/* Header row */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          sx={{ color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}
        >
          {label}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Chip
            label={type?.replace(/_/g, ' ')}
            size="small"
            sx={{ bgcolor: alpha(COLORS.purple, 0.15), color: COLORS.purple, fontWeight: 600, height: 20, fontSize: '0.6rem' }}
          />
          {constraintLabel && (
            <Chip
              label={constraintLabel}
              size="small"
              sx={{ bgcolor: alpha(COLORS.pink, 0.15), color: COLORS.pink, fontWeight: 600, height: 20, fontSize: '0.6rem' }}
            />
          )}
          {timeDiff && (
            <Chip
              label={timeDiff}
              size="small"
              sx={{ bgcolor: alpha(COLORS.warning, 0.15), color: COLORS.warning, fontWeight: 600, height: 20, fontSize: '0.6rem' }}
            />
          )}
          {offsetInfo && (
            <Chip
              label={offsetInfo}
              size="small"
              sx={{ bgcolor: alpha(COLORS.warning, 0.15), color: COLORS.warning, fontWeight: 600, height: 20, fontSize: '0.6rem' }}
            />
          )}
        </Stack>
      </Stack>

      {/* Signal boxes */}
      {isSingle ? (
        <SignalBox label={leftLabel} icon={leftIcon} signal={leftSignal} color={leftColor} />
      ) : (
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <SignalBox label={leftLabel} icon={leftIcon} signal={leftSignal} color={leftColor} />
          </Grid>
          <Grid item xs={6}>
            <SignalBox label={rightLabel} icon={rightIcon} signal={rightSignal} color={rightColor} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

// ==================== TRADE ROW ====================
const TradeRow = ({ trade, isExpanded, onToggle, allVariantIds }) => {
  const pnl = parseFloat(trade.pnl) || 0;
  const pnlPct = parseFloat(trade.pnl_pct) || 0;
  const adjustedPnlPct = parseFloat(trade.adjusted_pnl_pct) || 0;
  const isWin = pnl >= 0;
  const isAdjustedWin = adjustedPnlPct >= 0;
  const pnlColor = isWin ? COLORS.success : COLORS.danger;
  const adjustedPnlColor = isAdjustedWin ? COLORS.success : COLORS.danger;

  const requiredSignals = trade.required_signals || {};
  const confirmations = trade.confirmations || {};
  const requiredCount = Object.keys(requiredSignals).length;
  const confirmationCount = Object.keys(confirmations).length;

  const cellStyle = {
    borderBottom: isExpanded ? 'none' : `1px solid ${alpha(COLORS.border, 0.5)}`,
    py: 1.5,
  };

  return (
    <>
      <TableRow
        onClick={onToggle}
        sx={{
          cursor: 'pointer',
          bgcolor: isExpanded ? alpha(pnlColor, 0.03) : 'transparent',
          transition: 'background-color 0.15s ease',
          '&:hover': { bgcolor: alpha(COLORS.primary, 0.05) },
        }}
      >
        {/* Trade Number */}
        <TableCell sx={cellStyle}>
          <Typography variant="body2" sx={{ color: COLORS.primary, fontWeight: 700, fontFamily: 'monospace' }}>
            #{trade.trade_number}
          </Typography>
        </TableCell>

        {/* Status (WIN/LOSS) */}
        <TableCell sx={cellStyle}>
          <Chip
            icon={isWin ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
            label={isWin ? 'WIN' : 'LOSS'}
            size="small"
            sx={{
              bgcolor: alpha(pnlColor, 0.15), color: pnlColor,
              fontWeight: 700, fontSize: '0.7rem', height: 26,
              '& .MuiChip-icon': { color: pnlColor },
            }}
          />
        </TableCell>

        {/* Variant */}
        <TableCell sx={cellStyle}>
          {trade.variant_id ? (
            <Tooltip title={`Variant: ${trade.variant_label || trade.variant_id}`}>
              <Chip
                icon={<VariantIcon sx={{ fontSize: 12 }} />}
                label={trade.variant_label || trade.variant_id}
                size="small"
                sx={{
                  bgcolor: alpha(getVariantColor(trade.variant_id, allVariantIds), 0.15),
                  color: getVariantColor(trade.variant_id, allVariantIds),
                  fontWeight: 700, fontSize: '0.6rem', height: 22, maxWidth: 160,
                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                  '& .MuiChip-icon': { color: getVariantColor(trade.variant_id, allVariantIds) },
                }}
              />
            </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ color: alpha(COLORS.text.muted, 0.5), fontSize: '0.75rem' }}>
              —
            </Typography>
          )}
        </TableCell>

        {/* Symbol */}
        <TableCell sx={cellStyle}>
          <Chip
            label={trade.symbol || 'NQ'}
            size="small"
            sx={{ bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary, fontWeight: 600, height: 24, fontSize: '0.75rem' }}
          />
        </TableCell>

        {/* Entry Sig */}
        <TableCell sx={cellStyle}>
          {trade.entry_signal ? (
            <Chip
              label={trade.entry_signal}
              size="small"
              sx={{ bgcolor: alpha(COLORS.success, 0.12), color: COLORS.success, fontWeight: 700, fontSize: '0.6rem', height: 22 }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: alpha(COLORS.text.muted, 0.5), fontSize: '0.75rem' }}>—</Typography>
          )}
        </TableCell>

        {/* Entry Time */}
        <TableCell sx={cellStyle}>
          <Typography variant="body2" sx={{ color: COLORS.text.secondary, fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {formatDateTime(trade.entry_time)}
          </Typography>
        </TableCell>

        {/* Entry Price */}
        <TableCell sx={cellStyle}>
          <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 600, fontFamily: 'monospace' }}>
            {formatPrice(trade.entry_price)}
          </Typography>
        </TableCell>

        {/* Exit Sig */}
        <TableCell sx={cellStyle}>
          {trade.exit_signal ? (
            <Chip
              label={trade.exit_signal}
              size="small"
              sx={{ bgcolor: alpha(COLORS.danger, 0.12), color: COLORS.danger, fontWeight: 700, fontSize: '0.6rem', height: 22 }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: alpha(COLORS.text.muted, 0.5), fontSize: '0.75rem' }}>—</Typography>
          )}
        </TableCell>

        {/* Exit Time */}
        <TableCell sx={cellStyle}>
          <Typography variant="body2" sx={{ color: COLORS.text.secondary, fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {formatDateTime(trade.exit_time)}
          </Typography>
        </TableCell>

        {/* Exit Price */}
        <TableCell sx={cellStyle}>
          <Typography variant="body2" sx={{ color: COLORS.danger, fontWeight: 600, fontFamily: 'monospace' }}>
            {formatPrice(trade.exit_price)}
          </Typography>
        </TableCell>

        {/* Duration */}
        <TableCell sx={cellStyle}>
          <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
            {formatDuration(trade.entry_time, trade.exit_time)}
          </Typography>
        </TableCell>

        {/* P&L ($) */}
        <TableCell sx={cellStyle}>
          <Typography variant="body2" sx={{ color: pnlColor, fontWeight: 700, fontFamily: 'monospace' }}>
            {formatPnL(pnl)}
          </Typography>
        </TableCell>

        {/* P&L % */}
        <TableCell sx={cellStyle}>
          <Typography variant="body2" sx={{ color: pnlColor, fontWeight: 600, fontFamily: 'monospace' }}>
            {formatPercent(pnlPct)}
          </Typography>
        </TableCell>

        {/* Avg (Adjusted P&L %) */}
        <TableCell sx={cellStyle}>
          <Typography variant="body2" sx={{ color: adjustedPnlColor, fontWeight: 600, fontFamily: 'monospace' }}>
            {formatPercent(adjustedPnlPct)}
          </Typography>
        </TableCell>

        {/* High */}
        <TableCell sx={cellStyle}>
          {trade.trade_high != null ? (
            <Tooltip title={trade.high_time ? `At ${formatDateTime(trade.high_time)}` : ''}>
              <Stack spacing={0.25}>
                <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {formatPrice(trade.trade_high)}
                </Typography>
                {trade.high_pct != null && (
                  <Typography variant="caption" sx={{ color: COLORS.success, fontSize: '0.65rem', fontFamily: 'monospace' }}>
                    +{trade.high_pct}%
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ color: alpha(COLORS.text.muted, 0.5), fontSize: '0.75rem' }}>—</Typography>
          )}
        </TableCell>

        {/* Low */}
        <TableCell sx={cellStyle}>
          {trade.trade_low != null ? (
            <Tooltip title={trade.low_time ? `At ${formatDateTime(trade.low_time)}` : ''}>
              <Stack spacing={0.25}>
                <Typography variant="body2" sx={{ color: COLORS.danger, fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {formatPrice(trade.trade_low)}
                </Typography>
                {trade.low_pct != null && (
                  <Typography variant="caption" sx={{ color: COLORS.danger, fontSize: '0.65rem', fontFamily: 'monospace' }}>
                    {trade.low_pct}%
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ color: alpha(COLORS.text.muted, 0.5), fontSize: '0.75rem' }}>—</Typography>
          )}
        </TableCell>

        {/* Signals */}
        <TableCell sx={cellStyle}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              label={`R:${requiredCount}`}
              size="small"
              sx={{ bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary, fontWeight: 600, fontSize: '0.65rem', height: 20 }}
            />
            <Chip
              label={`C:${confirmationCount}`}
              size="small"
              sx={{ bgcolor: alpha(COLORS.warning, 0.1), color: COLORS.warning, fontWeight: 600, fontSize: '0.65rem', height: 20 }}
            />
            {isExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 18, color: COLORS.text.muted }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18, color: COLORS.text.muted }} />
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* Expanded Signal Details */}
      <TableRow>
        <TableCell colSpan={17} sx={{ p: 0, borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}` }}>
          <Collapse in={isExpanded}>
            <Box
              sx={{
                p: 2.5,
                bgcolor: alpha(COLORS.bg.tertiary, 0.4),
                borderTop: `1px solid ${alpha(COLORS.border, 0.3)}`,
              }}
            >
              <Grid container spacing={3}>
                {requiredCount > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: COLORS.primary, fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <BoltIcon sx={{ fontSize: 18 }} />
                      Required Signals ({requiredCount})
                    </Typography>
                    {Object.entries(requiredSignals).map(([id, signalData]) => (
                      <SignalDisplay
                        key={id}
                        label={id}
                        data={signalData}
                        color={COLORS.primary}
                      />
                    ))}
                  </Grid>
                )}
                {confirmationCount > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: COLORS.warning, fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 18 }} />
                      Confirmations ({confirmationCount})
                    </Typography>
                    {Object.entries(confirmations).map(([id, signalData]) => (
                      <SignalDisplay
                        key={id}
                        label={id}
                        data={signalData}
                        color={COLORS.warning}
                      />
                    ))}
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ==================== MAIN TABLE COMPONENT ====================
const TradeTable = ({ trades, expandedTrades, onToggleTrade }) => {
  const headers = [
    { key: '#', color: COLORS.text.muted },
    { key: 'Status', color: COLORS.text.muted },
    { key: 'Variant', color: COLORS.primary },
    { key: 'Symbol', color: COLORS.text.muted },
    { key: 'Entry Sig', color: COLORS.success },
    { key: 'Entry Time', color: COLORS.text.muted },
    { key: 'Entry', color: COLORS.text.muted },
    { key: 'Exit Sig', color: COLORS.danger },
    { key: 'Exit Time', color: COLORS.text.muted },
    { key: 'Exit', color: COLORS.text.muted },
    { key: 'Duration', color: COLORS.text.muted },
    { key: 'P&L', color: COLORS.text.muted },
    { key: 'P&L %', color: COLORS.text.muted },
    { key: 'Avg', color: COLORS.text.muted },
    { key: 'High', color: COLORS.success },
    { key: 'Low', color: COLORS.danger },
    { key: 'Signals', color: COLORS.text.muted },
  ];

  // Collect ordered unique variant IDs for consistent color assignment
  const allVariantIds = [...new Set(trades.map(t => t.variant_id).filter(Boolean))];

  if (!trades?.length) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <HistoryIcon sx={{ fontSize: 64, color: alpha(COLORS.text.muted, 0.3), mb: 2 }} />
        <Typography variant="h6" sx={{ color: COLORS.text.muted, mb: 0.5 }}>
          No trades found
        </Typography>
        <Typography variant="body2" sx={{ color: alpha(COLORS.text.muted, 0.6) }}>
          Trades will appear here once they are recorded
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: alpha(COLORS.bg.tertiary, 0.6) }}>
            {headers.map((header) => (
              <TableCell
                key={header.key}
                sx={{
                  color: header.color, fontWeight: 700, fontSize: '0.7rem',
                  textTransform: 'uppercase', letterSpacing: 0.8,
                  borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`, py: 1.5,
                }}
              >
                {header.key}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {trades.map((trade) => (
            <TradeRow
              key={trade.id || trade.trade_number}
              trade={trade}
              isExpanded={expandedTrades[trade.id || trade.trade_number] || false}
              onToggle={() => onToggleTrade(trade.id || trade.trade_number)}
              allVariantIds={allVariantIds}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TradeTable;
